import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../lib/api';
import type { UserCompetencyProgress, IngestorEvent, MyTeam, EventAward } from '../../lib/types';
import { QuestsSection } from './QuestsSection';
import { KudosSection } from './KudosSection';
import { MentorModeCard } from './MentorModeCard';
import { useCelebrations } from './useCelebrations';
import { CelebrationOverlay } from './CelebrationOverlay';
import { TeamRewardsCard } from '../coins/TeamRewardCard';
import { explainNoAwards } from './explain-no-awards.js';
import { RawEventModal } from './RawEventModal';

interface ProgressResponse {
  progress: UserCompetencyProgress[];
  total_xp: number;
  total_level: number;
  streak: { current: number; longest: number; freeze_count: number };
  class_auto: string;
  class_choice: string | null;
}

interface EventsResponse {
  events: (IngestorEvent & { ingestor_type: string; ingestor_name: string })[];
}

interface PlayerBadge {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  icon: string;
  earned: boolean;
  awarded_at: string | null;
}

interface DailyQuest {
  id: string;
  name: string;
  description: string;
  competency_id: string;
  competency_name: string;
  xp: number;
  progress: number;
  target_count: number;
  completed: boolean;
}

interface DailyQuestsResponse {
  date: string;
  quests: DailyQuest[];
  all_completed: boolean;
}

interface WeeklyChallenge {
  id: string; name: string; description: string; competency_name: string;
  xp: number; progress: number; target_count: number; completed: boolean;
}
interface WeeklyChallengesResponse {
  week: string; challenges: WeeklyChallenge[]; all_completed: boolean;
}

// M5.11: pick a short identifier for the event. For
// Jira issues we use the issue key (YOLO-1234); for
// GitHub we use the PR number; for everything else we
// fall back to the technical id. The full title
// renders {ingestor_type} · {eventId} · {rule_description}
// (e.g. 'jira · YOLO-6517 · closed a bug').
function pickEventId(e: { ingestor_type?: string; type: string; payload: Record<string, unknown>; target_external: string | null }): string {
  const p = e.payload ?? {};
  // Jira — issue_key is normalised on every Jira event.
  // v0.6.2: append the comment_id when present so that
  // multiple comments on the same issue_key are
  // distinguishable in the recent events list. Without
  // this, three comments on YOLO-6627 render as three
  // 'YOLO-6627' rows and the operator assumes they are
  // duplicates. They aren't — each comment_id is its
  // own event_id and the dedup key is (event_id,
  // rule_id, competency_id). The label-suffix 'c'
  // stands for 'comment'.
  const issueKey = typeof p.issue_key === 'string' ? p.issue_key : null;
  if (issueKey) {
    const commentId = typeof p.comment_id === 'string' && p.comment_id.length > 0 ? p.comment_id : null;
    return commentId ? `${issueKey} · c${commentId}` : issueKey;
  }
  // GitHub PRs — '#NN' (the user reads '#1042' as a
  // PR id much more naturally than 'pr_number:1042').
  // v0.6.2: same idea for review_state == 'commented'
  // reviews — append the review_id so one review
  // doesn't render as seven duplicates of '#2696'.
  const prNumber = typeof p.pr_number === 'number' ? p.pr_number : null;
  if (prNumber !== null) {
    const reviewId = typeof p.review_id === 'string' && p.review_id.length > 0 ? p.review_id : null;
    return reviewId ? `#${prNumber} · r${reviewId}` : `#${prNumber}`;
  }
  // Quests — quest_title is the human-readable id.
  const questTitle = typeof p.quest_title === 'string' ? p.quest_title : null;
  if (questTitle) return questTitle;
  // Kudos — target_external is the receiver handle.
  return e.target_external ?? `${e.type}`;
}

export function PlayerDashboardPage() {
  const me = useQuery({
    queryKey: ['me'],
    // The /api/me endpoint still returns opt_in_public_board (the
    // column stays in the data model in case the board is ever
    // re-opened), but the player dashboard no longer reads it —
    // the "Hide my name" toggle was removed on 2026-07-16 when the
    // board was locked down to auth-only. The type below omits
    // opt_in_public_board to make the dead-ness obvious at the
    // type level; the field is still on the wire.
    queryFn: () => api<{ user: { id: string; display_name: string; handle: string; role: string; is_ignored?: boolean } }>('/api/me'),
  });
  // M5.8: pull the user's team list at the page level so
  // both MyTeamCard and TeamRewardsCard can render against
  // the same data without each one duplicating the query.
  const myTeams = useQuery({
    queryKey: ['me-teams'],
    queryFn: () => api<{ teams: MyTeam[] }>('/api/me/teams'),
  });
  const teams = myTeams.data?.teams ?? [];
  const progress = useQuery({
    queryKey: ['me-progress'],
    queryFn: () => api<ProgressResponse>('/api/me/progress'),
  });
  // Fetch the competency list so we can render full names
  // (e.g. "Coding & Debugging") next to the per-competency XP. The
  // id is still useful for screen readers / debug so we keep it as
  // a small caption.
  const competencies = useQuery({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Array<{ id: string; name: string }> }>('/api/competencies'),
    staleTime: 60_000,
  });
  const events = useQuery({
    queryKey: ['me-events'],
    queryFn: () => api<EventsResponse>('/api/me/events?limit=20'),
  });
  // v0.6.2: stable local. The api always returns
  // `{events: [...]}` when authenticated, but the TS
  // type contract is compile-time only — at runtime the
  // query can resolve to `{}` (stale cache, partial
  // response, race condition). The previous
  // `events.data?.events.length` and `events.data!.events.map`
  // crashed with "can't access property 'length', i.data.events
  // is undefined" when `events.data.events` was undefined.
  // The same pattern bit KudosSection and StatusTransitions
  // in the previous commits (c1182d, e71e4d2). Local variable
  // + ?? [] makes every downstream access safe.
  const eventsList = events.data?.events ?? [];
  const badges = useQuery({
    queryKey: ['me-badges'],
    queryFn: () => api<{ badges: PlayerBadge[] }>('/api/me/badges'),
  });
  const dailyQuests = useQuery({
    queryKey: ['me-daily-quests'],
    queryFn: () => api<DailyQuestsResponse>('/api/me/daily-quests'),
    refetchInterval: 10_000,
  });
  const weeklyChallenges = useQuery({
    queryKey: ['me-weekly-challenges'],
    queryFn: () => api<WeeklyChallengesResponse>('/api/me/weekly-challenges'),
    refetchInterval: 30_000,
  });

  const user = me.data?.user;
  const totalXp = progress.data?.total_xp ?? 0;
  const totalLevel = progress.data?.total_level ?? 0;
  const streakCurrent = progress.data?.streak?.current ?? 0;
  const streakLongest = progress.data?.streak?.longest ?? 0;
  const freezeCount = progress.data?.streak?.freeze_count ?? 0;
  const classAuto = progress.data?.class_auto ?? 'none';
  const classChoice = progress.data?.class_choice ?? null;
  const effectiveClass = classChoice && classChoice !== 'none' ? classChoice : classAuto;
  // (classIcon map moved to SettingsPage's ClassSection in
  // v0.8.9. The dashboard no longer renders the class name
  // outside the celebration overlay.)
  // v0.7.23: proper-case display label for the class,
  // used in the page header ('Rick Tonoli, Guardian of
  // the Realm'). The api's CLASS_LABELS is the
  // authoritative mapping; this is the same shape
  // defined in apps/api/src/scoring/loot-and-classes.ts.
  // Duplicated here intentionally to keep the page
  // self-contained (the api contract for /api/me is
  // class_choice / class_auto as the enum slug, not the
  // display label).
  const classLabel = (cls: string): string => {
    const map: Record<string, string> = {
      crafter: 'Crafter',
      guardian: 'Guardian',
      explorer: 'Explorer',
      diplomat: 'Diplomat',
    };
    return map[cls] ?? cls;
  };
  // M5.7: optInPublic + the "Hide my name" toggle were removed on
  // 2026-07-16 when the public board was locked down to auth-only.
  // The /api/me endpoint still returns opt_in_public_board (the
  // column stays in case the board is ever re-opened), and the
  // POST /api/me/public-board-opt-in endpoint still works, but
  // there's no UI to change it. If you ever want to re-surface
  // a "show/hide my name" toggle, this is the place.
  const xpInLevel = totalXp - Math.pow(totalLevel, 1.5) * 100; // crude
  const xpForNextLevel = Math.pow(totalLevel + 1, 1.5) * 100 - Math.pow(totalLevel, 1.5) * 100;
  const progressPct = totalLevel === 0 && totalXp === 0
    ? 0
    : Math.min(100, Math.max(0, (xpInLevel / Math.max(1, xpForNextLevel)) * 100));

  const qc = useQueryClient();
  // (Class picker state + setClassChoice moved to
  // /settings/SettingsPage.tsx in v0.8.9. The dashboard
  // still reads effectiveClass below for the level-up
  // celebration overlay label, but no longer renders the
  // class select.)
  // M5.10: tracks which event the user clicked "View raw"
  // on. The modal renders when non-null. Using a focused
  // state instead of a URL search param because the modal
  // is transient and shouldn't be navigable. Closing the
  // modal (X / ESC / backdrop click) clears it back to null.
  const [rawEventId, setRawEventId] = useState<string | null>(null);
  // (setClassChoice moved to /settings/SettingsPage.tsx
  // ClassSection in v0.8.9. The dashboard no longer mutates
  // class directly.)
  const celebrationQueue = useCelebrations({
    badges: badges.data?.badges,
    totalLevel: progress.data?.total_level,
    streak: progress.data?.streak?.current,
    allDailyCompleted: dailyQuests.data?.all_completed,
  });
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Recent events is a long raw-activity list; collapse it by
  // default so the player's summary cards stay above the fold.
  const [recentEventsOpen, setRecentEventsOpen] = useState(false);
  const visibleCeleb = celebrationQueue.filter((c) => !dismissed.has(c.id));

  return (
    // v0.8.9: card-enter on the outer wrapper so every .card
    // section (stat tiles, today's quests, badges, kudos,
    // recent events, ...) rises + fades in with a 40ms stagger.
    // The inner 4-stat-tile grid used to have card-enter
    // directly — it's still active via the outer selector,
    // but since the outer stagger is faster (cards 1-8 by
    // document order, not just the 4 tiles), the inner
    // wrapper's nth-child rules collide with the outer ones.
    // Remove the inner card-enter to avoid double-staggered
    // animation on the stat tiles.
    <div className="space-y-6 card-enter">
      <CelebrationOverlay queue={visibleCeleb} onDismiss={(id) => setDismissed((s) => new Set(s).add(id))} />
      {/* M5.10: render the raw event modal as a sibling
          of CelebrationOverlay so both can stack at the
          same z-index if a celebration fires while the
          modal is open. The modal pulls its data from
          the existing events query cache — no refetch
          needed. Closes via X / ESC / backdrop click. */}
      {rawEventId ? (
        <RawEventModal
          event={(events.data?.events ?? []).find((ev) => ev.id === rawEventId) ?? {
            id: rawEventId,
            type: '?',
            payload: {},
            raw: null,
            actor_external: null,
            target_external: null,
            occurred_at: new Date().toISOString(),
            scored_at: null,
          }}
          onClose={() => setRawEventId(null)}
        />
      ) : null}
      <header>
        <div className="flex items-start justify-between gap-4">
          <div>
            {/*
              v0.7.23: header now reads
                '<name>, <Class> of the Realm'
              when the user has earned a class (the auto-
              suggested or player-chosen crafter / guardian /
              explorer / diplomat). When the user has no
              class yet (effectiveClass === 'none', e.g. a
              brand-new player or one who hasn't accrued
              enough XP for the auto-suggestion), the
              header falls back to the display name only
              — 'of the Realm' with no class would read
              odd.

              The phrase 'of the Realm' is the operator's
              chosen fantasy framing (Forbury / the Forbury
              Realm); consistent with the chronicles page's
              fantasy vocabulary.
            */}
            <h1 className="text-2xl font-semibold">
              {user?.display_name ?? '...'}
              {effectiveClass !== 'none' ? (
                <span className="text-ink-muted">
                  {', '}
                  {classLabel(effectiveClass)}
                  {' of the Realm'}
                </span>
              ) : null}
            </h1>
          </div>
          {/* v0.7.25: GDPR self-service export. Click
              triggers the browser's native download flow
              (the endpoint sets Content-Disposition:
              attachment + a timestamped filename). We
              use a plain <a href> rather than fetch +
              Blob because the api already sets the right
              headers; a fetch + Blob would force the
              user through an extra dialog. */}
          <a
            href="/api/me/export"
            download
            className="shrink-0 text-xs text-ink-muted underline-offset-4 hover:text-ink hover:underline"
            title="Download a JSON export of everything the system stores about you (GDPR Article 15 + 20)"
          >
            Export my data
          </a>
        </div>
        {user?.is_ignored ? (
          <div role="status" className="mt-3 rounded-md border border-ink-dim/40 bg-bg-elevated p-3 text-sm">
            <p className="font-medium text-ink-dim">This account is marked as ignored.</p>
            <p className="text-ink-muted">
              Your activity is recorded for audit, but you don't earn XP, badges, or appear on the public board.
              Contact an admin if this is wrong.
            </p>
          </div>
        ) : null}
      </header>

      {/* v0.8.9: stat tiles promoted to the top of the dashboard,
          right under the title. The operator wanted the headline
          numbers (Level / Total XP / Competencies / Streak) to be
          the first thing seen on landing. card-enter on the outer
          space-y-6 wrapper still applies the staggered rise. */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="card">
          <p className="label">Level</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{totalLevel}</p>
        </div>
        <div className="card">
          <p className="label">Total XP</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{totalXp}</p>
        </div>
        <div className="card">
          <p className="label">Competencies in progress</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{(progress.data?.progress ?? []).length ?? 0}</p>
        </div>
        <div className="card">
          <p className="label">Streak</p>
          <p className="mt-1 flex items-center gap-2 text-4xl font-semibold tabular-nums">
            <span className={streakCurrent > 0 ? '' : 'opacity-40'}>🔥</span>
            {streakCurrent}
          </p>
          <p className="mt-0.5 text-xs text-ink-dim">
            Longest {streakLongest} · {freezeCount} freeze{freezeCount === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {/* v0.8.9: XP progress bar promoted from below the daily
          quests block to right under the 4 stat tiles — the
          operator wants the level progression next to the
          headline Level / XP / Competencies / Streak numbers.
          Same data shape; gold gradient + recessed track +
          raised glossed fill, matching the public board's
          adventure progress bar so the brand reads consistently
          across surfaces. */}
      <div className="card">
        <p className="label">Progress to level {totalLevel + 1}</p>
        <div className="mt-2 rounded-full border border-white/10 bg-black/40 p-[2px] shadow-inner">
          <div
            className="relative h-3 rounded-full bg-gradient-to-b from-[var(--fantasy-gold)] to-black shadow-[0_2px_6px_rgba(0,0,0,0.5)] transition-all"
            style={{ width: `${progressPct.toFixed(1)}%` }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/35 to-transparent" />
          </div>
        </div>
        <p className="mt-1 text-xs text-ink-muted">
          {Math.max(0, Math.round(xpInLevel))} / {Math.max(1, Math.round(xpForNextLevel))} XP
        </p>
      </div>

      {/* v0.8.9: Badges promoted from below the Kudos section to
          right under the XP progress bar. The operator wants
          achievement signals next to the level-progression signal. */}
      <div className="card">
        <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Badges</h2>
        <p className="mb-2 text-xs text-ink-dim">
          Achievement badges earned by hitting specific milestones (e.g. first contribution, streak thresholds). Locked badges show as ? and unlock automatically when the rule fires.
        </p>
        {badges.isLoading ? (
          <p className="mt-2 text-ink-muted">Loading…</p>
        ) : ((badges.data?.badges ?? []).length ?? 0) === 0 ? (
          <p className="mt-2 text-ink-muted">No badges defined yet.</p>
        ) : (
          <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {(badges.data?.badges ?? []).map((b) => (
              <li
                key={b.id}
                className={
                  'flex flex-col items-center rounded-md p-2 text-center ' +
                  (b.earned ? 'bg-bg-elevated' : 'bg-bg-elevated/40')
                }
                title={b.earned ? `${b.name} — ${b.description}` : 'Locked badge'}
              >
                <span className={'text-3xl ' + (b.earned ? '' : 'opacity-30 grayscale')}>{b.earned ? b.icon : '❓'}</span>
                <span className={'mt-1 text-xs ' + (b.earned ? 'text-ink' : 'text-ink-dim')}>{b.earned ? b.name : '???'}</span>
                {b.earned ? (
                  <span className="text-[10px] uppercase tracking-wide text-ink-dim">{b.rarity}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* v0.8.9: combined "Your Team & Mentor Mode" card —
          the operator merged these because they're both about
          who you're working with. Two columns inside a single
          .card frame so we don't get nested notched corners.
          MentorModeCard and MyTeamCard both accept `bare` to
          drop their own .card wrapper. The mentor form still
          owns its own state (useState inside MentorModeCard).
          The previous standalone <MentorModeCard /> above was
          removed in this commit — the bare variant inside the
          combined card replaces it. */}
      <div className="card md:grid md:grid-cols-2 md:gap-6">
        <div>
          <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Your team{teams.length !== 1 ? 's' : ''}
          </h2>
          <MyTeamCard teams={teams} bare />
        </div>
        <div className="mt-4 md:mt-0 md:border-l md:border-white/5 md:pl-6">
          <MentorModeCard bare />
        </div>
      </div>

      {/* (4-stat-tile grid was promoted to the top of the
          dashboard in this commit — the duplicate block here
          is removed.) */}

      {/* (MyTeamCard moved into the combined card below.) */}

      {/* M5.8: team rewards. Show one card per team the
          user is on (multi-team users see one per team;
          the rare case). The card lists the top 3 rewards
          (by progress) with a View all link to the full
          list. Per ADR-0015, rewards are team-scoped, so
          a multi-team user has independent economies on
          each team. */}
      {myTeams.isLoading ? null : teams.map((t) => (
        <TeamRewardsCard key={t.id} team={t} />
      ))}

      {/* (Class card moved to /settings in v0.8.9 — the operator
          wanted profile-class customization to live next to the
          other self-service profile fields.) */}

      {dailyQuests.data ? (
        <div className="card">
          <div className="flex items-baseline justify-between">
            <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Today&#39;s quests</h2>
            <span className="text-xs text-ink-dim">Auto-fills from your work · refreshes 10s</span>
          </div>
          {dailyQuests.data.all_completed ? (
            <p className="mt-2 text-sm text-accent-success">
              🎯 Perfect day! All 3 quests complete (+10 bonus XP).
            </p>
          ) : null}
          <ul className="mt-3 space-y-2">
            {(dailyQuests.data?.quests ?? []).map((q) => (
              <li key={q.id} className="flex items-center gap-3 rounded-md bg-bg-elevated px-3 py-2">
                <span className="text-lg">{q.completed ? '✅' : q.progress > 0 ? '🔄' : '⬜'}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{q.name}</p>
                  <p className="text-xs text-ink-dim">{q.description || q.competency_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs tabular-nums text-ink-muted">
                    {q.progress}/{q.target_count}
                  </p>
                  <p className="text-xs text-ink-dim">+{q.xp} XP</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {((weeklyChallenges.data?.challenges ?? []).length) > 0 ? (
        <div className="card">
          <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">This week's challenges</h2>
          <p className="mb-2 text-xs text-ink-dim">Week-long goals — reset every Monday.</p>
          <ul className="mt-1 space-y-2">
            {(weeklyChallenges.data?.challenges ?? []).map((c) => (
              <li key={c.id} className="flex items-center gap-3 rounded-md bg-bg-elevated px-3 py-2">
                <span className="text-lg">{c.completed ? '✅' : c.progress > 0 ? '🔄' : '⬜'}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{c.name}</p>
                  <p className="text-xs text-ink-dim">{c.description || c.competency_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs tabular-nums text-ink-muted">{c.progress}/{c.target_count}</p>
                  <p className="text-xs text-ink-dim">+{c.xp} XP</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* (XP progress bar moved under the stat tiles in v0.8.9.) */}

      {/* v0.8.9: Per-competency XP card removed from the
          dashboard — it's the same data you see under /skills,
          and the operator wanted to declutter. The skill-tree
          page is the canonical home for per-competency detail. */}

      {/* (Badges card moved under the XP progress bar in v0.8.9.) */}

      <div className="card">
        <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Kudos</h2>
        <p className="mb-2 text-xs text-ink-dim">Recognize a teammate — they get +3 XP to Communication.</p>
        <KudosSection />
      </div>

      <div className="card">
        <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Quests</h2>
        <p className="mb-2 text-xs text-ink-dim">
          Pick up quests and mark them done to earn XP. Admin may need to confirm before XP is awarded.
        </p>
        <QuestsSection />
      </div>

      <div className="card">
        <button
          type="button"
          onClick={() => setRecentEventsOpen((v) => !v)}
          className="flex w-full items-center justify-between text-left"
          aria-expanded={recentEventsOpen}
        >
          <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Recent events</h2>
          <span className="text-ink-muted" aria-hidden>{recentEventsOpen ? '▾' : '▸'}</span>
        </button>
        {recentEventsOpen ? (
        <>
        <p className="mb-2 text-xs text-ink-dim">
          Raw activity events attributed to you from all your connected ingestors. XP awards are derived from these via scoring rules — the list here is the source, the XP list above is the result.
        </p>
        {events.isLoading ? (
          <p className="mt-2 text-ink-muted">Loading…</p>
        ) : eventsList.length === 0 ? (
          <p className="mt-2 text-ink-muted">No events yet attributed to you.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {eventsList.map((e) => (
              <li key={e.id} className="rounded-md bg-bg-elevated px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  {/* M5.11: human-readable title as the
                      primary line. The technical
                      ingestor_type/event_type pair is
                      small + muted underneath, so the
                      row reads 'what happened' first
                      and 'where it came from' on
                      hover or when the summary isn't
                      useful (e.g. kudos). Truncate at
                      1 line with full text in the title
                      tooltip for long Jira summaries. */}
                  {/* M5.11: title shows the ingestor /
                      event identifier (jira · YOLO-1234 /
                      github · #1042 / manual · quest
                      name) plus the optional description
                      of the scoring rule that fired for
                      this event. Format:
                        'jira · YOLO-6517 · closed a bug'
                      If the rule has no description, the
                      trailing dot + text is omitted:
                        'jira · YOLO-6517'
                      Admins can write free-form
                      descriptions on scoring rules in
                      /admin/scoring-rules. The awards
                      join below carries the description
                      onto the event so no per-event
                      backend lookup is needed. The
                      description (when present) is muted
                      so the leading 'jira · YOLO-6517'
                      stays scannable. */}
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate font-mono text-xs text-ink"
                      title={(() => {
                        const id = pickEventId(e);
                        const desc = e.awards?.find((a) => a.rule_description)?.rule_description ?? null;
                        return desc ? `${e.ingestor_type} · ${id} · ${desc}` : `${e.ingestor_type} · ${id}`;
                      })()}
                    >
                      {e.ingestor_type} · {pickEventId(e)}
                      {e.awards?.find((a) => a.rule_description)?.rule_description ? (
                        <>
                          {' · '}
                          <span className="text-ink-muted">
                            {e.awards?.find((a) => a.rule_description)?.rule_description}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {/* M5.10: View raw button per event row.
                        Opens a centered modal with both the
                        normalised payload (what scoring
                        rules read) and the raw upstream
                        response (what Jira/GitHub actually
                        sent). Useful when writing rules:
                        "is the field I want in the payload?"
                        → check payload tab. "is the upstream
                        actually sending it?" → check raw tab. */}
                    <button
                      type="button"
                      className="rounded-md px-1.5 py-0.5 text-xs text-ink-muted hover:bg-bg-panel hover:text-ink"
                      onClick={() => setRawEventId(e.id)}
                      title="Show the raw upstream payload + the normalised payload"
                    >
                      View raw
                    </button>
                    <p className="text-xs text-ink-dim">{new Date(e.occurred_at).toLocaleString()}</p>
                  </div>
                </div>
                {/* M5.11: the rule description is now in
                    the title line. target_external is
                    suppressed here because for the common
                    cases (Jira issue_key, GitHub #NN) the
                    title already carries the identifier;
                    only kudos / manual events lack a
                    tailored title. We render target_external
                    as a subtle sub-line for those so the
                    row isn't blank. */}
                {e.ingestor_type === 'kudos' && e.target_external ? (
                  <p className="mt-1 text-xs text-ink-muted">to {e.target_external}</p>
                ) : null}
                {e.awards && e.awards.length > 0 ? (
                  <div className="mt-1">
                    {(() => {
                      // M5.8: when an event has awards from multiple
                      // sources (e.g. rule + daily quest both fired
                      // for the same pr.closed), show a one-line
                      // summary so the player doesn't have to do
                      // mental math. "+18 XP (10 rule + 8 daily)".
                      // Single-source events get no summary (the
                      // per-award badges already show the source).
                      // M5.9+: include the 3 legacy source values
                      // ('streak', 'daily_bonus', 'quest') so the
                      // breakdown shows them too. Pre-M5.9 data
                      // has these in the DB; the totals dict
                      // would otherwise silently omit them from
                      // the "+X = N rule + M daily" summary.
                      // Typed as Record<EventAward['source'], ...>
                      // so TypeScript knows the keys are all
                      // there; without the explicit type the
                      // Record<string, number> makes every
                      // access a possible-undefined.
                      type Source = EventAward['source'];
                      const totals: Record<Source, number> = {
                        rule: 0, daily: 0, weekly: 0, admin_grant: 0,
                        streak: 0, daily_bonus: 0, quest: 0,
                      };
                      let totalXp = 0;
                      for (const a of e.awards) {
                        totals[a.source] += a.xp;
                        totalXp += a.xp;
                      }
                      const sourcesWithXp = (Object.keys(totals) as Array<Source>).filter((k) => totals[k] > 0);
                      const showSummary = sourcesWithXp.length > 1;
                      // v0.6.2: render the per-event coin total
                      // (e.coins, summed server-side from
                      // coin_transaction) inline with the
                      // per-award XP chips so the row reads
                      // as one line: "[+1 XP Communication]
                      // [+1 XP Ownership] [+2 🪙]". The XP
                      // chips and the coin chip are siblings
                      // in the same flex-wrap list — they
                      // wrap together on small screens, and
                      // the coin chip lands at the end so the
                      // eye reads the XP breakdown first.
                      // The coin chip is muted to match the
                      // non-XP award source labels ('streak',
                      // 'admin') so the row stays visually
                      // consistent. e.coins default-zero (event
                      // with no coin rows) renders as 0 🪙,
                      //   suppressed by the `e.coins > 0` guard
                      //   so the chip doesn't appear on
                      //   XP-only events.
                      const coinsPart = e.coins > 0 ? `${e.coins} 🪙` : null;
                      return (
                        <>
                          {showSummary ? (
                            <p className="mb-1 text-xs text-ink-muted">
                              +{totalXp} XP total
                              {sourcesWithXp.map((k, idx) => (
                                <span key={k}>
                                  {' = '}
                                  <span className="text-ink">{totals[k]}</span> {k}
                                  {idx < sourcesWithXp.length - 1 ? ' +' : ''}
                                </span>
                              ))}
                            </p>
                          ) : null}
                          <ul className="flex flex-wrap items-center gap-1.5">
                            {e.awards.map((a, i) => {
                      const comp = (competencies.data?.competencies ?? []).find((c) => c.id === a.competency_id);
                      // M5.8: surface the source so players can see where each
                      // award came from. The same event can fire both a
                      // scoring rule AND a daily/weekly quest, so without
                      // this breakdown the totals look like double-counting.
                      // Color matches the source so it's skimmable: rule =
                      // accent (the action's "default" award), daily =
                      // success-tinted (positive reinforcement), weekly =
                      // info-tinted (bigger goal).
                      // M5.9: 'admin' is the new source for admin grants
                      // (manual XP/coins via /api/admin/users/:id/grant).
                      // Gray/neutral — this is an adjustment, not positive
                      // reinforcement. The audit log has the full breakdown
                      // (admin id, reason, full xp+coin list).
                      // M5.9+ fix: xp_award.source was widened
                      // in migration 0028 to allow 3 legacy
                      // values ('streak', 'daily_bonus', 'quest')
                      // that exist in old data. The TS type
                      // didn't add them; rows with these
                      // sources still come back over the wire
                      // and would crash the render with
                      // 'undefined.class'. Default to a neutral
                      // muted style and the source value as the
                      // label so the UI is still usable. The
                      // EventAward TS type is also a lie about
                      // these values; tightening the contract is
                      // a separate cleanup.
                      const sourceMeta: Record<string, { label: string; class: string }> = {
                        rule:        { label: 'rule',   class: 'bg-accent/15 text-accent' },
                        daily:       { label: 'daily',  class: 'bg-accent-success/15 text-accent-success' },
                        weekly:      { label: 'weekly', class: 'bg-accent-warn/15 text-accent-warn' },
                        admin_grant: { label: 'admin',  class: 'bg-ink-dim/20 text-ink-muted' },
                        // Legacy values. Render neutrally — no
                        // player-facing "this came from a
                        // streak" or "this came from a quest"
                        // semantics in the modern model. The
                        // audit log has the full breakdown.
                        streak:      { label: 'streak',      class: 'bg-ink-dim/20 text-ink-muted' },
                        daily_bonus: { label: 'daily bonus', class: 'bg-ink-dim/20 text-ink-muted' },
                        quest:       { label: 'quest',       class: 'bg-ink-dim/20 text-ink-muted' },
                      };
                      const sm = sourceMeta[a.source] ?? {
                        label: a.source,
                        class: 'bg-ink-dim/20 text-ink-muted',
                      };
                      return (
                        <li key={i} className="flex items-center gap-1 rounded bg-bg-elevated/60 px-1.5 py-0.5 text-xs">
                          <span className="tabular-nums text-ink">+{a.xp} XP</span>
                          <span className="text-ink-dim">·</span>
                          <span className="text-ink-muted">{comp?.name ?? a.competency_id}</span>
                          <span
                            className={`ml-1 rounded px-1 py-0.5 text-[10px] uppercase tracking-wide ${sm.class}`}
                            title={
                              a.source === 'rule'
                                ? 'Awarded by a scoring rule for this event'
                                : a.source === 'daily'
                                ? 'Awarded by a daily quest (objective-based bonus)'
                                : a.source === 'weekly'
                                ? 'Awarded by a weekly challenge (objective-based bonus)'
                                : a.source === 'admin_grant'
                                ? 'Awarded by an admin manual grant (M5.9, ADR-0017)'
                                : a.source === 'streak'
                                ? 'Awarded by a streak bonus (legacy source)'
                                : a.source === 'daily_bonus'
                                ? 'Awarded by a daily completion bonus (legacy source)'
                                : a.source === 'quest'
                                ? 'Awarded by a quest completion (legacy source)'
                                : `Awarded via source: ${a.source}`
                            }
                          >
                            {sm.label}
                          </span>
                        </li>
                      );
                    })}
                            {coinsPart ? (
                              <li className="flex items-center gap-1 rounded bg-bg-elevated/60 px-1.5 py-0.5 text-xs">
                                <span className="tabular-nums text-ink">+{coinsPart}</span>
                                <span className="text-ink-dim">·</span>
                                <span className="text-ink-muted">coin</span>
                              </li>
                            ) : null}
                          </ul>
                        </>
                      );
                    })()}
                  </div>
                ) : e.scored_at ? (
                  // v0.6.2: the "no awards" line was misleading.
                  // The old text 'No awards (scored before
                  // evidence tracking)' was a one-size-fits-all
                  // message from M3 when `scored_at` could be set
                  // without awards being recorded. Today, the
                  // most common cause is a real event that the
                  // scoring engine evaluated and produced no
                  // awards for (e.g. a PR review with
                  // review_state='commented' matches no rule).
                  // Surface a contextual reason so the player
                  // understands WHY this didn't credit XP.
                  <p className="mt-1 text-xs text-ink-dim">{explainNoAwards(e)}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        </>
        ) : null}
      </div>
    </div>
  );
}

// "Your team" card on the player dashboard. Shows the current user's
// teams + role_in_team + member count. Hidden if they're on no
// team. Multiple teams render as a list.
function MyTeamCard({ teams, bare = false }: { teams: MyTeam[]; bare?: boolean }) {
  if (teams.length === 0) {
    const empty = (
      <p className="mt-1 text-sm text-ink-muted">
        You're not on a team yet. Ask an admin to add you to one — teams are how kudos,
        scoring rules, quests, and the public board are scoped.
      </p>
    );
    if (bare) return empty;
    return (
      <div className="card">
        <p className="label">Your team</p>
        {empty}
      </div>
    );
  }
  const list = (
    <ul className="mt-2 space-y-2">
      {teams.map((t) => (
        <li key={t.id} className="flex items-center justify-between rounded-md bg-bg-elevated px-3 py-2">
          <div className="flex items-center gap-3">
            {t.colour ? (
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: t.colour }} aria-hidden />
            ) : null}
            <div>
              <p className="font-medium">{t.name}</p>
              <p className="text-xs text-ink-dim">@{t.slug} · {t.member_count} member{t.member_count === 1 ? '' : 's'}</p>
            </div>
          </div>
          <span className="rounded bg-bg-panel px-2 py-0.5 text-xs uppercase tracking-wide text-ink-muted">
            {t.role_in_team}
          </span>
        </li>
      ))}
    </ul>
  );
  if (bare) return list;
  return (
    <div className="card">
      <p className="label">Your team{teams.length > 1 ? 's' : ''}</p>
      {list}
    </div>
  );
}
